import {
  Autocomplete,
  FormControl,
  FormLabel,
  AutocompleteOption as JoyAutocompleteOption,
  ListItemContent,
  ListItemDecorator,
} from '@mui/joy';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type IconName } from '@fortawesome/fontawesome-svg-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type AutocompleteOption, type TAutocompleteOption } from '~/common/types';
import RelationType from '../../../enums/RelationType';

import { isValidEmailAddress } from '../../../utils/helpers';
import { search } from '../../../utils/constants';
import { trpc } from '@/config/trpc';
import useDebounce from '../../../hooks/UseDebounce';

import AvatarWithColor from '../AvatarWithColor';
import FormHelperText from '../forms/FormHelperText';

type SelectEmailAddressProps = {
  value: string | string[] | undefined;
  onChange: (option: string | string[] | undefined) => void;
  label?: string;
  required?: boolean;
  multiple?: boolean;
  error?: string | undefined;
  touched?: boolean;
};

const SelectEmailAddress: React.FC<SelectEmailAddressProps> = ({
  onChange,
  value,
  multiple = true,
  label,
  error,
  touched,
  ...rest
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const { data: emailAddressesAutocomplete, isLoading: isEmailAddressesAutocompleteLoading } =
    trpc.mailbox.searchAutoComplete.useQuery(debouncedQuery, {
      enabled: debouncedQuery.length >= search.START_SEARCH_QUERY_LENGTH,
    });

  const className = classNames('max-h-[200px]', {
    'overflow-y-auto overflow-x-hidden before:hidden': multiple && value && value.length >= 10,
  });

  const handleKeyDown = (e: any) => {
    if (e.code === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (isValidEmailAddress(query)) {
        onChange(
          multiple
            ? value
              ? [...(value as string[]).filter((ea: string) => ea !== query), query]
              : [query]
            : query,
        );
      }
    }
  };
  return (
    <FormControl>
      {label && <FormLabel>{label}</FormLabel>}
      <Autocomplete
        loading={isEmailAddressesAutocompleteLoading}
        options={emailAddressesAutocomplete ?? []}
        onInputChange={(_, val) => setQuery(val.toLowerCase())}
        className={className}
        onChange={(_, val) =>
          onChange(
            multiple
              ? (val as string[]).map((v) =>
                  typeof v === 'string' ? v : (v as AutocompleteOption).value,
                )
              : typeof val === 'string'
              ? val
              : (val as TAutocompleteOption<string>).value,
          )
        }
        groupBy={
          emailAddressesAutocomplete?.length
            ? (option) => {
                if (option.data?.type === RelationType.Contact) return t('contacts');
                if (option.data?.type === RelationType.Company) return t('companies');
                return '';
              }
            : undefined
        }
        inputValue={query}
        onKeyDownCapture={handleKeyDown}
        value={value}
        disableClearable
        filterOptions={(options, params) => {
          const { inputValue } = params;

          const isExisting = options.some((option) => option.value === inputValue);

          if (inputValue !== '' && !isExisting && isValidEmailAddress(inputValue)) {
            options.push({
              data: {
                customIcon: 'plus',
                customOptionLabel: t('useThisAddress', { address: inputValue }),
              },
              label: '',
              value: inputValue,
            });
          }

          if (inputValue.length < search.START_SEARCH_QUERY_LENGTH) {
            options.push({
              data: {
                customIcon: 'pen-line',
                customOptionLabel: t('startTypingToSearchNetwork'),
                disabled: true,
              },
              label: '',
              value: '',
            });
          }

          return options;
        }}
        multiple={multiple}
        freeSolo={query.length < search.START_SEARCH_QUERY_LENGTH}
        clearOnBlur
        renderOption={(props, option) => (
          <JoyAutocompleteOption {...props} key={option.value}>
            <ListItemDecorator>
              {option?.data?.customIcon ? (
                <FontAwesomeIcon icon={['fas', option?.data?.customIcon as IconName]} />
              ) : (
                <AvatarWithColor name={option.value} size="sm" />
              )}
            </ListItemDecorator>
            <ListItemContent className="flex items-baseline text-sm">
              {option?.data?.customOptionLabel ? (
                <span>{option.data?.customOptionLabel as string}</span>
              ) : (
                <>
                  <span>{option?.label as string}</span>{' '}
                  <span className="ml-1 text-xs">[{option.value}]</span>
                </>
              )}
            </ListItemContent>
          </JoyAutocompleteOption>
        )}
        getOptionLabel={(option) =>
          typeof option === 'string' ? (option as string) : option.value
        }
        getOptionDisabled={(option) => option?.data?.disabled as boolean}
        noOptionsText={t('common:noOptions')}
        {...rest}
      />
      <FormHelperText error={error} touched={touched} />
    </FormControl>
  );
};

export default SelectEmailAddress;
